import { useState, useEffect, useCallback, useId, useContext, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { ActionPageContext } from "./actionPage";
import { Loader2, AlertTriangle, RotateCw, Inbox, ArrowLeft, ShieldX, SearchX, Eye, EyeOff, Lock, FileText, UserRound, Info as InfoIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { api, formatApiErrorDetail } from "@/lib/api";
import { draftKey, dropDraft, peekDraft, saveDraft } from "@/lib/drafts";

export const can = (user, perm) => !!(user?.permissions?.includes("*") || user?.permissions?.includes(perm));

export function errText(err) {
  if (!err?.response) return "Keine Verbindung zum Server. Bitte Netzwerk prüfen und erneut versuchen.";
  if (err.response.status === 401) return "Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.";
  const detail = err.response.data?.detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail) && detail.message) return detail.message;
  return formatApiErrorDetail(detail);
}

export function fmt(iso, timeZone) {
  if (!iso) return "–";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const opts = { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" };
  try {
    return d.toLocaleString("de-CH", timeZone ? { ...opts, timeZone } : opts);
  } catch (_) {
    return d.toLocaleString("de-CH", opts);
  }
}

export function fmtDay(iso) {
  if (!iso) return "–";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export const deText = (v) => (v && typeof v === "object" ? v.de ?? "" : v ?? "");

export function useLoad(path) {
  const [state, setState] = useState({ data: null, error: null, status: null, loading: true });
  const load = useCallback(async () => {
    if (!path) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await api.get(path);
      setState({ data, error: null, status: 200, loading: false });
    } catch (err) {
      setState({ data: null, error: errText(err), status: err.response?.status ?? 0, code: err.response?.data?.detail?.code, loading: false });
    }
  }, [path]);
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
}

export function Loading({ label = "Wird geladen …" }) {
  return (
    <div className="flex items-center justify-center gap-2 py-14 text-sm text-muted-foreground" role="status" data-testid="loading-state">
      <Loader2 className="h-6 w-6 animate-spin text-brand-magenta" aria-hidden="true" /> {label}
    </div>
  );
}

function BlockedRow({ label: text, value }) {
  return (
    <div className="rounded-xl border border-[#E6E1F0] bg-white px-4 py-3">
      <p className="text-sm font-semibold text-zinc-500">{text}</p><p className="mt-1 text-base text-brand-purple">{value}</p>
    </div>
  );
}

export function BlockedAside({ icon: Icon, title, text, lavender }) {
  return (
    <div className={`rounded-2xl border p-4 ${lavender ? "border-[#E3DDF0] bg-[#EEEAF7]" : "border-[#E6E1F0] bg-white"}`}>
      <p className="flex items-center gap-2 font-bold text-brand-purple"><Icon className="h-5 w-5 text-brand-magenta" aria-hidden="true" />{title}</p>
      <p className="mt-2 text-sm text-zinc-600">{text}</p>
    </div>
  );
}

// C120: organisation-bound accounts see a clear, server-confirmed block instead of foreign organisation data.
function ForeignOrgBlocked() {
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <div role="alert" data-testid="foreign-org-blocked" className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18.5rem]">
    <div className="min-w-0 space-y-2.5">
      <h1 className="text-3xl font-bold text-brand-purple">Zugriff auf fremde Organisation gesperrt</h1>
      <p className="text-base text-zinc-600">Fremde Organisationsdaten werden in diesem Bereich nicht angezeigt.</p>
      <div className="flex items-center gap-3 rounded-xl bg-[#EEEAF7] px-4 py-5">
        <Lock className="h-8 w-8 shrink-0 text-brand-purple" aria-hidden="true" />
        <div><p className="text-lg font-bold text-brand-purple">Bereich nicht freigegeben</p><p className="text-sm text-zinc-600">{user?.role_label}</p></div>
      </div>
      <BlockedRow label="Rolle" value={user?.role_label} />
      <BlockedRow label="Zielbereich" value="Nicht freigegebene Organisation" />
      <BlockedRow label="Zugriff" value="Verweigert" />
      <BlockedRow label="Nächster Schritt" value="Zu eigenen Aufgaben und erlaubten Bereichen zurückkehren" />
      <div className="flex flex-col gap-3 pt-2 sm:flex-row">
        <Button onClick={() => navigate("/aufgaben")} data-testid="foreign-org-to-tasks" className="h-11 bg-brand-magenta px-6 text-base font-bold hover:bg-brand-magenta-dark sm:min-w-[17rem]">Zu eigenen Aufgaben</Button>
        <Button variant="outline" onClick={() => navigate(-1)} data-testid="foreign-org-back" className="h-11 px-6 text-base font-bold text-brand-purple sm:min-w-[10rem]">Zurück</Button>
      </div>
    </div>
      <aside className="hidden space-y-4 pt-24 lg:block" aria-label="Kontext" data-testid="foreign-org-aside">
        <BlockedAside icon={FileText} title="Zuständigkeit" text={user?.role_label} lavender />
        <BlockedAside icon={UserRound} title="Verantwortung" text={`${user?.name} · ${user?.role_label}`} />
        <BlockedAside icon={InfoIcon} title="Prüfstand" text="Entscheidung und erforderliche Nachweise vor einer Bestätigung prüfen." />
      </aside>
    </div>
  );
}

export function ErrorBox({ error, status, code, onRetry }) {
  if (code === "foreign_organization") return <ForeignOrgBlocked />;
  const Icon = status === 403 ? ShieldX : status === 404 ? SearchX : AlertTriangle;
  const title = status === 403 ? "Keine Berechtigung" : status === 404 ? "Nicht gefunden" : "Laden fehlgeschlagen";
  return (
    <div role="alert" data-testid="error-state" className="flex flex-col items-center gap-3 rounded-2xl border border-red-200 bg-red-50 p-8 text-center">
      <Icon className="h-9 w-9 text-red-600" aria-hidden="true" />
      <p className="font-semibold text-red-800">{title}</p>
      <p className="max-w-lg text-sm text-red-800">{error}</p>
      {onRetry && status !== 403 && status !== 404 && (
        <Button variant="outline" size="sm" onClick={onRetry} data-testid="error-retry" className="gap-1.5">
          <RotateCw className="h-4 w-4" aria-hidden="true" /> Erneut versuchen
        </Button>
      )}
    </div>
  );
}

export function Loadable({ state, children }) {
  if (state.loading && !state.data) return <Loading />;
  if (state.error) return <ErrorBox error={state.error} status={state.status} code={state.code} onRetry={state.reload} />;
  if (!state.data) return null;
  return children(state.data);
}

export function Empty({ text = "Keine Einträge vorhanden.", testId = "empty-state" }) {
  return (
    <p data-testid={testId} className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
      <Inbox className="h-4 w-4" aria-hidden="true" /> {text}
    </p>
  );
}

const LABELS = {
  zur_pruefung: "Zur Prüfung", in_pruefung: "In Prüfung", "in_prüfung": "In Prüfung", in_bearbeitung: "In Bearbeitung",
  wartet_auf_mitglied: "Wartet auf Mitglied", "identität_prüfen": "Identität prüfen", keine_freigabe: "Keine Freigabe",
  "rückfrage": "Rückfrage", "veröffentlicht": "Veröffentlicht", "gelöst": "Gelöst", "übernommen": "Übernommen",
  in_app: "In der App", super_admin: "Super Admin", admin: "Administration", reviewer: "Prüfer:in",
  moderator: "Moderator:in", support: "Support", editor: "Redaktion", member: "Mitglied",
  sichtbar: "Sichtbar", ausgeblendet: "Ausgeblendet", entfernt_autor: "Von Verfasser:in entfernt",
  entfernt_moderation: "Von Moderation entfernt", org_staff: "Begrenzter Organisationszugang", warteliste: "Warteliste",
  angeboten: "Platz angeboten",
  storniert: "Storniert", "zuständigkeit_geprüft": "Zuständigkeit geprüft", nicht_bestanden: "Nicht bestanden",
};
export const label = (v) => (v == null || v === "" ? "–" : LABELS[v] || String(v).charAt(0).toUpperCase() + String(v).slice(1).replace(/_/g, " "));

const SUCCESS = ["aktiv", "verifiziert", "genehmigt", "freigegeben", "gelöst", "veröffentlicht", "versendet", "betriebsbereit",
  "bestätigt", "angenommen", "abgeschlossen", "behoben", "konfiguriert", "ja", "niedrig", "sichtbar", "zugeordnet", "bestanden", "geprüft"];
const WARNING = ["offen", "ausstehend", "zur_pruefung", "in_pruefung", "in_prüfung", "rückfrage", "angefragt", "übernommen",
  "in Bearbeitung", "in_bearbeitung", "geplant", "mittel", "wartet_auf_mitglied", "teilweise fehlgeschlagen", "untersuchung",
  "zu prüfen", "zur Prüfung vorgelegt", "identität_prüfen", "neu", "hoch", "wird versendet", "entschieden", "eingeschränkt", "ausgeblendet",
  "fehlt", "nicht zugeordnet", "warteliste", "angeboten", "zuständigkeit_geprüft", "zugewiesen", "teilweise offen"];
const CRITICAL = ["gesperrt", "abgelehnt", "kritisch", "abgesagt", "fehlgeschlagen", "gestört", "widerrufen", "dringend", "nein",
  "entfernt_moderation", "konflikt", "nicht_bestanden"];
const TONES = {
  success: "bg-green-100 text-green-800 border-green-300", warning: "bg-orange-100 text-orange-800 border-orange-300",
  critical: "bg-red-100 text-red-800 border-red-300", neutral: "bg-zinc-100 text-zinc-700 border-zinc-300",
};

export function Status({ value, testId }) {
  const tone = SUCCESS.includes(value) ? "success" : WARNING.includes(value) ? "warning" : CRITICAL.includes(value) ? "critical" : "neutral";
  return (
    <span data-testid={testId} className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${TONES[tone]}`}>
      {label(value)}
    </span>
  );
}

export function Section({ title, actions, children, testId, className = "" }) {
  return (
    <Card className={`border-border/70 ${className}`} data-testid={testId}>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0 pb-3">
        <h2 className="text-2xl font-semibold leading-snug text-brand-purple">{title}</h2>
        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

export function Info({ items }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
      {items.filter(Boolean).map(([k, v]) => (
        <div key={k}>
          <dt className="text-base font-medium leading-relaxed text-muted-foreground">{k}</dt>
          <dd className="mt-1 break-words text-lg font-medium leading-relaxed text-brand-purple">{v === null || v === undefined || v === "" ? "–" : v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function History({ items, testId = "history-list" }) {
  if (!items?.length) return <Empty text="Noch kein Verlauf." />;
  return (
    <ol className="space-y-3" data-testid={testId}>
      {[...items].reverse().map((h, i) => (
        <li key={i} className="border-l-2 border-brand-magenta/40 pl-3">
          <p className="text-sm font-semibold text-brand-purple">{h.action}</p>
          <p className="text-xs text-muted-foreground">{fmt(h.at)} · {h.by || "System"}{h.by_role ? ` (${label(h.by_role)})` : ""}</p>
          {h.reason && <p className="mt-0.5 text-sm text-zinc-700">Begründung: {h.reason}</p>}
          {h.note && <p className="mt-0.5 text-sm text-zinc-700">Interne Notiz: {h.note}</p>}
        </li>
      ))}
    </ol>
  );
}

export function BackLink({ to, children }) {
  return (
    <Link to={to} data-testid="back-link" className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-brand-purple hover:underline">
      <ArrowLeft className="h-4 w-4" aria-hidden="true" /> {children}
    </Link>
  );
}

const inputCls = "w-full rounded-lg border border-input bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-magenta";

export function SelectField({ id, label: text, value, onChange, options, testId, className = "" }) {
  return (
    <label htmlFor={id} className={`flex flex-col gap-1.5 text-sm font-bold text-brand-purple ${className}`}>
      {text}
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className={`${inputCls} h-11 font-normal`}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

export function TextField({ id, label: text, value, onChange, testId, type = "text", placeholder, className = "" }) {
  return (
    <label htmlFor={id} className={`flex flex-col gap-1.5 text-sm font-bold text-brand-purple ${className}`}>
      {text}
      <input id={id} type={type} value={value} placeholder={placeholder ?? "Noch nicht eingegeben"} onChange={(e) => onChange(e.target.value)}
        data-testid={testId} className={`${inputCls} h-11 font-normal`} />
    </label>
  );
}

export function passwordStrength(pw) {
  const n = (pw || "").length;
  if (!n) return null;
  if (n < 10) return { tone: "text-red-700", text: `Noch zu kurz: ${n} von mindestens 10 Zeichen.` };
  if (n < 14) return { tone: "text-orange-800", text: "Ausreichend. Länger ist sicherer, zum Beispiel ein Satz aus mehreren Wörtern." };
  return { tone: "text-green-800", text: "Gut: ein langes Passwort. Sonderzeichen sind nicht vorgeschrieben." };
}

export function PasswordInput({ id, value, onChange, testId, autoComplete, className = "", strength }) {
  const [show, setShow] = useState(false);
  const s = strength ? passwordStrength(value) : null;
  return (
    <>
      <span className="relative block">
        <input id={id} type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
          autoComplete={autoComplete} aria-describedby={s ? `${id}-strength` : undefined} className={`${className} w-full pr-11`} />
        <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? "Passwort verbergen" : "Passwort anzeigen"} aria-pressed={show}
          data-testid={`${testId}-toggle`}
          className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-lg text-zinc-600 hover:text-brand-purple focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-magenta">
          {show ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
        </button>
      </span>
      {s && <span id={`${id}-strength`} aria-live="polite" className={`text-xs ${s.tone}`} data-testid={`${testId}-strength`}>{s.text}</span>}
    </>
  );
}

function initial(fields) {
  return Object.fromEntries(fields.filter((f) => f.name).map((f) => [
    f.name, f.default ?? (f.type === "checkbox" ? false : f.type === "checks" ? [] : ""),
  ]));
}

function FieldInput({ f, value, set, testId, invalid, errorId }) {
  const id = `${testId}-${f.name}`;
  const aria = invalid ? { "aria-invalid": true, "aria-describedby": errorId } : {};
  if (f.type === "info") return <p className="rounded-lg bg-secondary/70 p-3 text-sm text-zinc-700">{f.text}</p>;
  if (f.type === "checkbox") {
    return (
      <label htmlFor={id} className="flex items-start gap-2 text-sm text-zinc-800">
        <input id={id} type="checkbox" checked={!!value} onChange={(e) => set(e.target.checked)} data-testid={id} {...aria} className="mt-0.5 h-4 w-4 accent-[#D50B58]" />
        <span>{f.label}{f.required ? " *" : ""}</span>
      </label>
    );
  }
  const head = <span className="field-head text-sm font-medium text-zinc-800">{f.label}{f.required ? " *" : ""}</span>;
  if (f.type === "file") {
    return (
      <label htmlFor={id} className="flex flex-col gap-1">
        {head}
        <span className="flex flex-wrap items-center gap-3 rounded-lg focus-within:ring-2 focus-within:ring-brand-magenta focus-within:ring-offset-2">
          <span className="inline-flex h-10 cursor-pointer items-center rounded-lg border border-brand-purple/30 bg-white px-4 text-sm font-semibold text-brand-purple hover:bg-secondary">Datei auswählen</span>
          <span className="text-sm text-zinc-600" data-testid={`${id}-chosen`}>{value ? `${value.name} · ${Math.max(1, Math.round(value.size / 1024))} KB` : "Keine Datei ausgewählt"}</span>
        </span>
        <input id={id} type="file" accept={f.accept} onChange={(e) => set(e.target.files?.[0] || "")} data-testid={id} {...aria} className="sr-only" />
        {f.help && <span className="text-xs text-muted-foreground">{f.help}</span>}
      </label>
    );
  }
  if (f.type === "radio") {
    return (
      <fieldset id={id} tabIndex={-1} {...aria} className="rounded-xl border border-[#E6E1F0] bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-magenta" data-testid={id}>
        <legend className="sr-only">{f.label}</legend>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1" aria-hidden="true">{head}{f.badge && <div>{f.badge(value)}</div>}</div>
          <div className="flex flex-wrap gap-x-5 gap-y-1.5">
            {f.options.map((o) => (
              <label key={o.value} className="flex items-center gap-1.5 text-sm text-zinc-700">
                <input type="radio" name={id} value={o.value} checked={value === o.value} onChange={() => set(o.value)}
                  data-testid={`${id}-${o.value}`} className="h-4 w-4 accent-[#D50B58]" />{o.label}
              </label>
            ))}
          </div>
        </div>
        {f.help && <p className="mt-1 text-xs text-muted-foreground">{f.help}</p>}
      </fieldset>
    );
  }
  if (f.type === "checks") {
    return (
      <fieldset id={id} tabIndex={-1} {...aria} className="space-y-1.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-magenta focus:ring-offset-2" data-testid={id}>
        <legend className="mb-1">{head}</legend>
        {f.options.map((o) => (
          <label key={o.value} className="flex items-center gap-2 text-sm text-zinc-800">
            <input type="checkbox" checked={value.includes(o.value)} data-testid={`${id}-${o.value}`} className="h-4 w-4 accent-[#D50B58]"
              onChange={(e) => set(e.target.checked ? [...value, o.value] : value.filter((v) => v !== o.value))} />
            {o.label}
          </label>
        ))}
        {f.help && <p className="text-xs text-muted-foreground">{f.help}</p>}
      </fieldset>
    );
  }
  return (
    <label htmlFor={id} className="flex flex-col gap-1">
      {head}
      {f.type === "textarea" ? (
        <textarea id={id} rows={f.rows || 4} value={value} onChange={(e) => set(e.target.value)} data-testid={id} {...aria} className={inputCls} maxLength={f.max} />
      ) : f.type === "select" ? (
        <select id={id} value={value} onChange={(e) => set(e.target.value)} data-testid={id} {...aria} className={`${inputCls} h-10`}>
          {f.placeholder !== false && <option value="">{f.placeholder || "Bitte wählen"}</option>}
          {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : f.type === "password" ? (
        <PasswordInput id={id} value={value} onChange={set} testId={id} autoComplete={f.autoComplete} strength={f.strength} className={`${inputCls} h-10`} />
      ) : (
        <input id={id} type={f.type || "text"} value={value} onChange={(e) => set(e.target.value)} data-testid={id} {...aria}
          className={`${inputCls} h-10`} min={f.min} max={f.maxValue} autoComplete={f.autoComplete} />
      )}
      {f.help && <span className="text-xs text-muted-foreground">{f.help}</span>}
    </label>
  );
}

function missingField(fields, values) {
  return fields.find((f) => {
    if (!f.required || !f.name) return false;
    const v = values[f.name];
    if (f.type === "checks") return !v?.length;
    if (f.type === "checkbox") return !v;
    return !String(v ?? "").trim();
  });
}

// C119: a save that lost a race against a newer version is shown as a conflict, never silently overwritten.
const CONFLICT_RE = /(gerade|zwischenzeitlich|inzwischen|neuere|andere(n)? Person|neu laden)/i;

function ConflictBox({ conflict, testId, onReload, onBack }) {
  const time = conflict.at.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" });
  return (
    <div role="alert" className="space-y-2.5" data-testid={`${testId}-conflict`}>
      <p className="text-lg font-bold text-brand-purple">Änderungskonflikt beim Speichern</p>
      <p className="text-sm text-zinc-600">Die neuere Version wird vor einer Übernahme sichtbar verglichen.</p>
      <div className="rounded-xl bg-[#EEEAF7] p-3"><p className="text-xs font-semibold text-zinc-500">Bisher / Eigene Fassung</p>
        <p className="font-bold text-brand-purple">Stand {time} · Noch nicht gespeichert</p></div>
      <div className="rounded-xl bg-[#FCE9F1] p-3"><p className="text-xs font-semibold text-zinc-500">Neu / Aktueller Vorschlag</p>
        <p className="font-bold text-brand-purple">{conflict.msg}</p></div>
      <BlockedRow label="Überschreiben" value="Noch nicht erfolgt" />
      <BlockedRow label="Nächster Schritt" value="Änderungen vergleichen und bewusst übernehmen" />
      <DialogFooter className="gap-2">
        <Button type="button" variant="outline" onClick={onBack} data-testid={`${testId}-conflict-back`}>Zurück zur eigenen Fassung</Button>
        <Button type="button" onClick={onReload} data-testid={`${testId}-conflict-reload`} className="bg-brand-magenta hover:bg-brand-magenta-dark">Neu laden und vergleichen</Button>
      </DialogFooter>
    </div>
  );
}

function DialogForm({ fields = [], submitLabel = "Bestätigen", onSubmit, onDone, close, danger, testId, children, preview, page, footerNote, cancelLabel = "Abbrechen" }) {
  const { user } = useAuth();
  const key = user?.id && testId ? draftKey(user.id, testId) : null;
  const [restored] = useState(() => (key ? peekDraft(key) : null));
  const [values, setValues] = useState(() => ({ ...initial(fields), ...(restored || {}) }));
  const valuesRef = useRef(values);
  valuesRef.current = values;
  useEffect(() => {
    if (key) dropDraft(key);
  }, [key]);
  useEffect(() => {
    if (!key) return undefined;
    // Session ending/expired: keep what was typed (no files, no passwords) for the next login in this tab.
    const save = () => saveDraft(key, fields, valuesRef.current, initial(fields));
    window.addEventListener("control:session-ending", save);
    window.addEventListener("control:session-expired", save);
    return () => {
      window.removeEventListener("control:session-ending", save);
      window.removeEventListener("control:session-expired", save);
    };
  }, [key, fields]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [shown, setShown] = useState(null);
  const [conflict, setConflict] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const missing = missingField(fields, values);
    if (missing) {
      setError({ msg: `Bitte „${missing.label}“ ausfüllen.`, field: missing.name });
      document.getElementById(`${testId}-${missing.name}`)?.focus();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await onSubmit(values);
      toast.success(res?.message || (typeof res === "string" ? res : "Gespeichert."));
      onDone && onDone();
      if (res?.show) { setShown(res.show); setBusy(false); return; }
      close(true);
    } catch (err) {
      const msg = errText(err);
      if (err.response?.status === 409 && CONFLICT_RE.test(msg)) setConflict({ msg, at: new Date() });
      else setError({ msg, field: null }); // server errors stay until the next submit
      setBusy(false);
    }
  };

  if (conflict) {
    return <ConflictBox conflict={conflict} testId={testId} onBack={() => setConflict(null)} onReload={() => { onDone && onDone(); close(); }} />;
  }
  if (shown) {
    return (
      <div className="space-y-4" data-testid={`${testId}-result`}>
        {shown}
        <DialogFooter><Button onClick={close} data-testid={`${testId}-close`}>Schließen</Button></DialogFooter>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className={page ? "space-y-4 [&_.field-head]:font-bold [&_.field-head]:text-brand-purple" : "space-y-4"} noValidate>
      {restored && (
        <p role="status" data-testid={`${testId}-draft-restored`} className="rounded-lg border border-[#E3DDF0] bg-[#EEEAF7] p-3 text-sm text-brand-purple">
          Entwurf aus der abgelaufenen Sitzung wiederhergestellt. Bitte prüfen und erneut speichern.
        </p>
      )}
      {children}
      {fields.map((f, i) => (
        <FieldInput key={f.name || i} f={f} value={values[f.name]} testId={testId} invalid={error?.field === f.name} errorId={`${testId}-error`}
          set={(v) => {
            setValues((s) => ({ ...s, [f.name]: v }));
            // Only the hint for this field disappears, and only once the correction satisfies it.
            setError((e) => (e?.field === f.name && !missingField([f], { [f.name]: v }) ? null : e));
          }} />
      ))}
      {preview && preview(values)}
      {page && footerNote}
      {error && <p role="alert" id={`${testId}-error`} data-testid={`${testId}-error`} className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error.msg}</p>}
      {page ? (
        <div className="flex flex-col gap-3 pt-2 sm:flex-row">
          <Button type="submit" disabled={busy} data-testid={`${testId}-submit`}
            className="h-11 bg-brand-magenta px-6 text-base font-bold hover:bg-brand-magenta-dark sm:min-w-[13rem]">
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />}{submitLabel}
          </Button>
          <Button type="button" variant="outline" onClick={close} data-testid={`${testId}-cancel`} className="h-11 px-6 text-base font-bold text-brand-purple sm:min-w-[8rem]">{cancelLabel}</Button>
        </div>
      ) : (
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={close} data-testid={`${testId}-cancel`}>Abbrechen</Button>
          <Button type="submit" disabled={busy} data-testid={`${testId}-submit`}
            className={danger ? "bg-red-600 hover:bg-red-700" : "bg-brand-magenta hover:bg-brand-magenta-dark"}>
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />}{submitLabel}
          </Button>
        </DialogFooter>
      )}
    </form>
  );
}

export function ActionDialog({ open, onOpenChange, title, description, fields = [], submitLabel = "Bestätigen", onSubmit, onDone, danger, testId = "action-dialog", children, preview }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid={testId}>
        <DialogHeader>
          <DialogTitle className="text-brand-purple">{title}</DialogTitle>
          <DialogDescription>{description || "Pflichtfelder sind mit * markiert."}</DialogDescription>
        </DialogHeader>
        {open && (
          <DialogForm fields={fields} submitLabel={submitLabel} onSubmit={onSubmit} onDone={onDone} danger={danger}
            close={() => onOpenChange(false)} testId={testId} preview={preview}>{children}</DialogForm>
        )}
      </DialogContent>
    </Dialog>
  );
}

const CRUMBS = {
  mitglieder: "Mitglieder", profilpruefungen: "Profilprüfungen", aktivitaeten: "Aktivitäten", "kreise-gruppen": "Kreise & Gruppen",
  "meldungen-sicherheit": "Meldungen & Sicherheit", organisationen: "Organisationen", support: "Support",
  "inhalte-freigaben": "Inhalte & Freigaben", benachrichtigungen: "Benachrichtigungen", einstellungen: "Einstellungen",
  konto: "Mein Konto", systemstatus: "Systemstatus", "audit-log": "Audit-Log", "pilot-auswertung": "Pilot & Auswertung",
  aufgaben: "Meine Aufgaben", "team-rechte": "Team & Rechte",
};

// Shared route logic for V3 full pages: `<detail>/<page>` or `<list>/aktion/<page>`; the origin page stays mounted but hidden.
function usePageRoute(page) {
  const params = useParams();
  const { pathname, search } = useLocation();
  const { host, enter, leave, mark } = useContext(ActionPageContext);
  const active = !!page && params.aktion === page;
  useEffect(() => {
    if (params.aktion) mark(params.aktion);
  }, [params.aktion, mark]);
  const base = params.aktion ? pathname.replace(/\/(aktion\/)?[^/]+$/, "") : pathname.replace(/\/$/, "");
  useEffect(() => {
    if (!active) return undefined;
    enter();
    return leave;
  }, [active, enter, leave]);
  return { active, host, params, back: `${base}${search}`, href: `${base}${params.id ? "" : "/aktion"}/${page}${search}` };
}

const rowId = (k) => String(k).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-");

export function PageRows({ rows, testId = "page" }) {
  return (rows || []).filter((r) => r && r[1] !== undefined && r[1] !== null && r[1] !== "").map(([k, v]) => (
    <div key={k} className="rounded-xl border border-[#E6E1F0] bg-white px-4 py-2.5" data-testid={`${testId}-row-${rowId(k)}`}>
      <p className="text-xs font-semibold text-zinc-500">{k}</p><div className="mt-0.5 break-words text-sm text-brand-purple">{v}</div>
    </div>
  ));
}

const pageBtn = "h-11 px-6 text-base font-bold";

export function PageButtons({ primary, onPrimary, primaryTo, primaryDisabled, busy, back, secondary = "Zurück", testId }) {
  return (
    <div className="flex flex-col gap-3 pt-2 sm:flex-row">
      {primary && (primaryTo ? (
        <Link to={primaryTo} data-testid={`${testId}-primary`} className={`${buttonVariants()} ${pageBtn} bg-brand-magenta hover:bg-brand-magenta-dark sm:min-w-[13rem]`}>{primary}</Link>
      ) : (
        <Button type="button" onClick={onPrimary} disabled={primaryDisabled || busy} data-testid={`${testId}-primary`}
          className={`${pageBtn} bg-brand-magenta hover:bg-brand-magenta-dark sm:min-w-[13rem]`}>
          {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />}{primary}
        </Button>
      ))}
      {back && <Link to={back} data-testid={`${testId}-back`} className={`${buttonVariants({ variant: "outline" })} ${pageBtn} text-brand-purple sm:min-w-[8rem]`}>{secondary}</Link>}
    </div>
  );
}

export function PageShell({ title, subtitle, testId, subject, aside, top, wide, children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  return (
    <div data-testid={`${testId}-page`} className="animate-fade-in-up">
      <p className="hidden text-xs font-semibold text-zinc-500 lg:block" data-testid="action-page-crumb">{CRUMBS[pathname.split("/")[1]] || "Übersicht"} / Plattformverwaltung</p>
      <p className="text-xs font-semibold text-zinc-500 lg:hidden">{user?.name} · {user?.role_label}</p>
      <h1 lang="de" className="mt-3 break-words text-3xl font-bold leading-tight tracking-tight text-brand-purple hyphens-auto lg:text-[2.15rem]">{title}</h1>
      {subtitle && <p className="mt-2 text-base text-zinc-600">{subtitle}</p>}
      {top && <div className="mt-5">{top}</div>}
      <div className={`mt-6 grid gap-6 ${wide ? "" : "lg:grid-cols-[minmax(0,1fr)_18.5rem]"}`}>
        <div className="min-w-0 space-y-3">{children}</div>
        {!wide && <aside className="hidden space-y-4 lg:block" aria-label="Kontext">
          {aside || (
            <>
              <BlockedAside icon={FileText} title="Ausgewählter Vorgang" text={subject || title} lavender />
              <BlockedAside icon={UserRound} title="Verantwortung" text={`${user?.name} · ${user?.role_label}`} />
              <BlockedAside icon={InfoIcon} title="Prüfstand" text="Entscheidung und erforderliche Nachweise vor einer Bestätigung prüfen." />
            </>
          )}
        </aside>}
      </div>
    </div>
  );
}

function PageLink({ href, testId, variant, className, icon: Icon, text, disabled }) {
  const cls = `gap-1.5 ${variant === "default" ? "bg-brand-magenta hover:bg-brand-magenta-dark" : ""} ${className}`;
  const inner = <>{Icon && <Icon className="h-4 w-4" aria-hidden="true" />}{text}</>;
  if (disabled) return <Button type="button" variant={variant} size="sm" disabled data-testid={testId} className={cls}>{inner}</Button>;
  return <Link to={href} data-testid={testId} className={`${buttonVariants({ variant, size: "sm" })} ${cls}`}>{inner}</Link>;
}

// V3 full-page view without a form (own route); children may be a function receiving { back }.
export function PageView({ page, label: text, icon, variant = "outline", testId, className = "", hideLink, title, subtitle, subject, aside, top, wide, children }) {
  const r = usePageRoute(page);
  const navigate = useNavigate();
  if (r.active) {
    return r.host ? createPortal(
      <PageShell title={title || text} subtitle={subtitle} testId={testId} subject={subject} aside={aside} top={top} wide={wide}>
        {typeof children === "function" ? children({ ...r, navigate }) : children}
      </PageShell>, r.host) : null;
  }
  return hideLink ? null : <PageLink href={r.href} testId={testId} variant={variant} className={className} icon={icon} text={text} />;
}

function ActionPageView({ title, subtitle, rows, notice, subject, testId, back, dialog, top, cancelLabel, aside }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { description, ...form } = dialog;
  // After a successful submit replace the form URL (browser back must not reopen it); keep a redirect done by onSubmit.
  const leave = (done) => {
    if (window.location.pathname !== pathname) return;
    navigate(back, { replace: done === true });
  };
  const tone = notice?.tone === "lavender" ? "border-[#E3DDF0] bg-[#EEEAF7] text-brand-purple" : "border-[#F6E3B4] bg-[#FFF6DD] text-[#8A5A00]";
  return (
    <PageShell title={title} subtitle={subtitle || description || "Pflichtfelder sind mit * markiert."} testId={testId} subject={subject} top={top} aside={aside}>
          <PageRows rows={rows} testId={testId} />
          {subtitle && description && <p className="rounded-xl border border-[#E3DDF0] bg-[#EEEAF7] px-4 py-3 text-sm text-zinc-700">{description}</p>}
          <DialogForm {...form} page cancelLabel={cancelLabel} close={(done) => leave(done)} testId={`${testId}-dialog`} footerNote={notice && (
            <div role="note" className={`rounded-xl border px-4 py-3 ${tone}`} data-testid={`${testId}-page-notice`}>
              <p className="flex items-center gap-2 text-sm font-bold"><InfoIcon className="h-4 w-4" aria-hidden="true" />{notice.title}</p>
              {notice.text && <p className="mt-1 text-xs text-zinc-600">{notice.text}</p>}
            </div>
          )} />
    </PageShell>
  );
}

// V3 full-page action (own route `<detail>/<page>` or `<list>/aktion/<page>`), same fields and endpoint as the dialog.
function PageAction({ label: text, icon, variant = "outline", testId, disabled, title, className = "", defaultOpen = false, hideLink,
  page, pageSubtitle, pageRows, pageNotice, pageSubject, pageTop, pageBack, pageAside, cancelLabel, ...dialog }) {
  const r = usePageRoute(page);
  const navigate = useNavigate();
  useEffect(() => {
    if (defaultOpen && !r.params.aktion) navigate(r.href, { replace: true });
  }, [defaultOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  if (r.active) {
    return r.host ? createPortal(<ActionPageView title={title || text} subtitle={pageSubtitle} rows={pageRows} notice={pageNotice}
      subject={pageSubject} testId={testId} back={pageBack || r.back} dialog={dialog} top={pageTop} cancelLabel={cancelLabel} aside={pageAside} />, r.host) : null;
  }
  return hideLink ? null : <PageLink href={r.href} testId={testId} variant={variant} className={className} icon={icon} text={text} disabled={disabled} />;
}

export function ActionButton(props) {
  return props.page ? <PageAction {...props} /> : <DialogAction {...props} />;
}

function DialogAction({ label: text, icon: Icon, variant = "outline", testId, disabled, title, className = "", defaultOpen = false, ...dialog }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <>
      <Button type="button" variant={variant} size="sm" onClick={() => setOpen(true)} disabled={disabled} data-testid={testId}
        className={`gap-1.5 ${variant === "default" ? "bg-brand-magenta hover:bg-brand-magenta-dark" : ""} ${className}`}>
        {Icon && <Icon className="h-4 w-4" aria-hidden="true" />}{text}
      </Button>
      <ActionDialog open={open} onOpenChange={setOpen} title={title || text} testId={`${testId}-dialog`} {...dialog} />
    </>
  );
}

export function RunButton({ label: text, icon: Icon, run, success, onDone, testId, variant = "outline", disabled }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await run();
      toast.success(success);
      onDone && onDone();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button type="button" variant={variant} size="sm" onClick={go} disabled={busy || disabled} data-testid={testId}
      className={`gap-1.5 ${variant === "default" ? "bg-brand-magenta hover:bg-brand-magenta-dark" : ""}`}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : Icon && <Icon className="h-4 w-4" aria-hidden="true" />}{text}
    </Button>
  );
}

export function saveBlob(data, filename, type = "text/csv;charset=utf-8") {
  const blob = data instanceof Blob ? data : new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function blobError(err) {
  const data = err.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return { ...err, response: { ...err.response, data: parsed } };
    } catch (_) {
      return err;
    }
  }
  return err;
}

export const COUNTRY_OPTIONS = [
  { value: "CH", label: "Schweiz" }, { value: "DE", label: "Deutschland" }, { value: "AT", label: "Österreich" },
];
export const TZ_OPTIONS = [
  { value: "Europe/Zurich", label: "Europe/Zurich (Schweiz)" }, { value: "Europe/Berlin", label: "Europe/Berlin (Deutschland)" },
  { value: "Europe/Vienna", label: "Europe/Vienna (Österreich)" },
];

// Instance-unique DOM ids. Every tab controls the one shared result panel, which is labelled by the active tab;
// an unknown selected value falls back to the first real tab.
export function useTabs(items, value) {
  const uid = useId().replace(/[^A-Za-z0-9_-]/g, "");
  const found = items.findIndex((it) => it.value === value);
  const index = found === -1 ? 0 : found;
  return { items, index, value: items[index].value, panelId: `tabs-${uid}-panel`, tabId: (i) => `tabs-${uid}-tab-${i}` };
}

export function TabPanel({ tabs, testId, children }) {
  return <div role="tabpanel" id={tabs.panelId} aria-labelledby={tabs.tabId(tabs.index)} data-testid={testId}>{children}</div>;
}

export function Tabbar({ tabs, onChange, testId, ariaLabel = "Filter" }) {
  const { items } = tabs;
  const keyNav = (e, index) => {
    const last = items.length - 1;
    const next = { ArrowRight: index === last ? 0 : index + 1, ArrowLeft: index === 0 ? last : index - 1, Home: 0, End: last }[e.key];
    if (next === undefined) return;
    e.preventDefault();
    onChange(items[next].value);
    e.currentTarget.parentElement.children[next]?.focus();
  };
  return (
    <div role="tablist" aria-label={ariaLabel} data-testid={testId} className="mb-4 flex flex-wrap gap-2">
      {items.map((it, index) => {
        const selected = index === tabs.index;
        return (
          <button key={it.value} type="button" role="tab" id={tabs.tabId(index)} aria-selected={selected} aria-controls={tabs.panelId}
            tabIndex={selected ? 0 : -1} onClick={() => onChange(it.value)} onKeyDown={(e) => keyNav(e, index)}
            data-testid={`${testId}-${String(it.value).replace(/\s+/g, "-")}`}
            className={`min-h-[40px] rounded-full border px-4 text-sm font-medium transition-colors ${selected ? "border-brand-purple bg-brand-purple text-white" : "border-border bg-white text-brand-purple hover:bg-secondary"}`}>
            {it.label}{it.count !== undefined ? ` (${it.count})` : ""}
          </button>
        );
      })}
    </div>
  );
}

export function RowLink({ to, children, testId }) {
  return <Link to={to} data-testid={testId} className="font-semibold text-brand-purple underline-offset-2 hover:underline">{children}</Link>;
}
